import { ReactElement } from 'react';
import { Container, Title } from './CPCOntologyOverviewPage.styled';
import { redirect } from '../../../helper/redirectHelper';
import cpcOverviewDiagram from './static/cpc-ontology-overview-diagram.png';
import Terms from '../../components/common/Terms';

const CPCOntologyOverviewPage = (): ReactElement => {
    return (
        <Container className="container">
            <Title data-testid="title">CPC Ontology Overview</Title>
            <p>
                The Cooperative Patent Classification (CPC) was initiated as a joint partnership between the{' '}
                <a href="https://www.uspto.gov/">United States Patent and Trademark Office (USPTO)</a> and the{' '}
                <a href="https://www.epo.org/">European Patent Office (EPO)</a> where the Offices have agreed to
                harmonize their existing classification systems (USPC and ECLA, respectively) and migrate towards a
                common classification scheme.
            </p>
            <p>
                The EP Linked Data Service publishes a linked data representation of the CPC classification scheme. The
                CPC ontology defines the classes and properties used to describe the linked data representation of CPC.
                For more information about CPC see{' '}
                <a href="https://www.cooperativepatentclassification.org/index.html">the CPC website</a>.
            </p>
            <p>
                The current linked data representation of CPC includes all the CPC scheme and concordance data with the{' '}
                <a href="https://www.wipo.int/">World Intellectual Property Organization</a>'s{' '}
                <a href="https://www.wipo.int/classifications/ipc/">International Patent Classification scheme (IPC)</a>
                . It does not include the CPC Definitions.
            </p>
            <p>
                The prefix normally used for terms in the CPC ontology is <span className="code">cpc:</span>. The
                ontology namespace is <a href={redirect('def/cpc')}>https://data.epo.org/def/cpc/</a>. Terms
                related to IPC are defined in the namespace{' '}
                <a href={redirect('def/ipc')}>https://data.epo.org/def/ipc/</a> with the normal prefix{' '}
                <span className="code">ipc:</span>. The URLs identifying all terms in the CPC ontology dereference and
                are also described in the <a href="documentation/cpc-ontology-reference">cpc ontology reference page</a>
                .
            </p>
            <p>An overview of the major classes in the CPC ontology is given in the following diagram:</p>
            <div>
                <img data-testid="diagramImage" src={cpcOverviewDiagram} alt="overview of CPC ontology" />
            </div>
            <p>
                The CPC classification scheme defines a hierarchy of classifications. The CPC ontology uses the{' '}
                <a href="https://www.w3.org/2004/02/skos/">Simple Knowledge Organization System (SKOS)</a> as a base
                ontology which it extends. SKOS is a mature well known base ontology which is widely used for
                representing classification hierarchies, taxonomies and controlled vocabularies.
            </p>
            <p>
                The central concept in the CPC ontology is{' '}
                <a href={redirect('def/cpc/Classification')}>cpc:Classification</a>. cpc:Classification is a subclass of{' '}
                <a href="https://www.w3.org/TR/2009/REC-skos-reference-20090818/#concepts">skos:Concept</a>. Each
                instance of cpc:Classification is a node in the CPC classification hierarchy. For example{' '}
                <a href={redirect('def/cpc/A01B1-16')}>A01B1/16</a> is a classification node in the CPC hierarchy.
            </p>
            <p>
                Classifications in the hierarchy are linked to their higher and lower classifications by the{' '}
                <a href="https://www.w3.org/TR/2009/REC-skos-reference-20090818/#semantic-relations">
                    SKOS semantic relation properties
                </a>{' '}
                skos:broader and skos:narrower respectively.
            </p>
            <p>
                The property <a href={redirect('def/cpc/symbol')}>cpc:symbol</a> relates a CPC classification to its
                symbol, e.g. "A01B1/16". The property <a href={redirect('def/cpc/sortKey')}>cpc:sortKey</a> relates a
                CPC classification to a key that may be used for ordering classifications. The property{' '}
                <a href={redirect('def/cpc/level')}>cpc:level</a> takes an integer value indicating the level of a
                classification in the hierarchy.
            </p>
            <p>Each classification has three title properties providing different variations of its title:</p>
            <ul>
                <li>
                    <a href={redirect('def/cpc/fullTitle')}>cpc:fullTitle</a>: a plain text representation of the title
                    including text, cpc only text enclosed in "{`{ }`}`" brackets and references enclosed in "[ ]"
                    brackets.
                </li>
                <li>
                    <a href="https://dublincore.org/documents/dcmi-terms/#elements-title">dcterms:title</a>: a plain
                    text representation of the title including text and cpc only text enclosed in "{`{ }`}" brackets but
                    excluding references. Note this property is from the{' '}
                    <a href="https://dublincore.org/documents/dcmi-terms/">Dublin Core Metadata Initiative</a> ontology.
                </li>
                <li>
                    <a href={redirect('def/cpc/titleXML')}>cpc:titleXML</a>: an XML representation of the full title.
                </li>
            </ul>
            <p>
                The resource <a href={redirect('def/cpc/scheme')}>cpc:scheme</a> represents the scheme as a whole.
                cpc:scheme is linked to the top level CPC classifications with the property
                <a href="https://www.w3.org/TR/2009/REC-skos-reference-20090818/#schemes">skos:hasTopConcept</a> and the
                top level concepts are related to the scheme with the property{' '}
                <a href="https://www.w3.org/TR/2009/REC-skos-reference-20090818/#schemes">skos:topConceptOf</a>.
            </p>
            <p>
                Roughly, cpc:scheme corresponds to level 1 of the CPC hierarchy. The following sub-classes of
                cpc:Classification correspond to classifications at lower levels:
            </p>
            <ul>
                <li data-testid="level2">
                    level 2: <a href={redirect('def/cpc/Section')}>Section</a>
                </li>
                <li data-testid="level3">
                    level 3: <a href={redirect('def/cpc/SubSection')}>SubSection</a>
                </li>
                <li>
                    level 4:
                    <a data-testid="level4Link" href={redirect('def/cpc/Class')}>
                        Class
                    </a>
                </li>
                <li>
                    level 5:
                    <a data-testid="level5Link" href={redirect('def/cpc/SubClass')}>
                        SubClass
                    </a>
                </li>
                <li>
                    level 7: <a href={redirect('def/cpc/MainGroup')}>MainGroup</a>
                </li>
                <li>
                    level &gt;7: <a href={redirect('def/cpc/SubGroup')}>SubGroup</a>
                </li>
            </ul>
            <p>
                CPC level 6 corresponds to the class
                <a href={redirect('def/cpc/GuidanceHeading')}>cpc:GuidanceHeading</a>. Guidance Headings are
                annotations, information for the CPC user and so they are not represented as classifications. The
                property <a href={redirect('def/cpc/guidanceHeading')}>cpc:guidanceHeading</a> links a main group to its
                guidance heading, if there is one.
            </p>
            <p>
                Many classifications in CPC are concordant with classifications in IPC. The property{' '}
                <a href={redirect('def/cpc/concordantIPC')}>cpc:concordantIPC </a>
                relates a CPC classification to its concordant IPC classification. The inverse property{' '}
                <a href={redirect('def/cpc/concordantCPC')}>cpc:concordantCPC </a> relates an IPC classification to its
                concordant IPC classification. The property <a href={redirect('def/ipc/symbol')}>ipc:symbol</a>{' '}
                indicates the symbol used to identify an IPC classification.
            </p>
            <Terms />
        </Container>
    );
};

export default CPCOntologyOverviewPage;
